import { RS_FEATURED_PUBLIC_BOARD_PATH } from '../constants/reservationFeaturedBoard.config';

/** /help ページの機能一覧・詳細ガイド定義（文言は i18n キーを参照） */

export type HelpSystemCard = {
  id: string;
  titleKey: string;
  descKey: string;
  linkKey: string;
  path: string;
  adminOnly?: boolean;
  loginRequired?: boolean;
  icon: string;
  iconColor?: string;
};

export type HelpUsageGuideKind =
  | 'steps'
  | 'reservation'
  | 'gene'
  | 'gaisuke'
  | 'transit'
  | 'access_logs';

export type HelpUsageGuide = {
  id: string;
  titleKey: string;
  kind: HelpUsageGuideKind;
  path: string;
  adminOnly?: boolean;
  loginRequired?: boolean;
  /** kind === 'steps' のとき */
  descKey?: string;
  stepsKey?: string[];
  noteKey?: string;
  /** kind === 'gene' | 'access_logs' の追加キー */
  extraKeys?: string[];
};

export const HELP_SYSTEM_CARDS: HelpSystemCard[] = [
  {
    id: 'keisuke',
    titleKey: 'home.feature_keisuke_title',
    descKey: 'home.feature_keisuke_description',
    linkKey: 'help.link_keisuke',
    path: '/keisuke',
    icon: 'fa-square-root-alt',
    iconColor: 'text-info',
  },
  {
    id: 'sample',
    titleKey: 'home.feature_sample_title',
    descKey: 'home.feature_sample_description',
    linkKey: 'help.link_sample',
    path: '/samples',
    loginRequired: true,
    icon: 'fa-flask',
    iconColor: 'text-primary',
  },
  {
    id: 'reservation',
    titleKey: 'home.feature_reservation_title',
    descKey: 'home.feature_reservation_description',
    linkKey: 'help.link_reservation',
    path: RS_FEATURED_PUBLIC_BOARD_PATH,
    icon: 'fa-calendar-check',
    iconColor: 'text-info',
  },
  {
    id: 'labo_suke',
    titleKey: 'home.feature_labo_suke_title',
    descKey: 'home.feature_labo_suke_description',
    linkKey: 'help.link_labo_suke',
    path: '/labo-suke',
    icon: 'fa-book',
    iconColor: 'text-success',
  },
  {
    id: 'transit',
    titleKey: 'home.feature_transit_pass_title',
    descKey: 'home.feature_transit_pass_description',
    linkKey: 'help.link_transit',
    path: '/transit-pass-pool',
    icon: 'fa-credit-card',
    iconColor: 'text-secondary',
  },
  {
    id: 'gaisuke',
    titleKey: 'home.feature_gaisuke_title',
    descKey: 'home.feature_gaisuke_description',
    linkKey: 'help.link_gaisuke',
    path: '/gaisuke',
    icon: 'fa-chart-line',
    iconColor: 'text-info',
  },
  {
    id: 'gaisuke_fx',
    titleKey: 'home.feature_gaisuke_fx_title',
    descKey: 'home.feature_gaisuke_fx_description',
    linkKey: 'home.feature_gaisuke_fx_cta',
    path: '/arena/fx',
    icon: 'fa-yen-sign',
    iconColor: 'text-info',
  },
  {
    id: 'keiba_arena',
    titleKey: 'keibaArena.title',
    descKey: 'home.feature_keiba_arena_description',
    linkKey: 'home.feature_keiba_arena_cta',
    path: '/arena/keiba',
    icon: 'fa-flag-checkered',
    iconColor: 'text-success',
  },
  {
    id: 'banquet',
    titleKey: 'banquet.title',
    descKey: 'home.feature_banquet_description',
    linkKey: 'home.feature_banquet_cta',
    path: '/arena/banquet',
    icon: 'fa-book-open',
    iconColor: 'text-danger',
  },
  {
    id: 'econ_report',
    titleKey: 'econReport.title',
    descKey: 'home.feature_econ_report_description',
    linkKey: 'home.feature_econ_report_cta',
    path: '/arena/econ',
    icon: 'fa-chart-line',
    iconColor: 'text-info',
  },
  {
    id: 'gene',
    titleKey: 'home.feature_gene_title',
    descKey: 'home.feature_gene_description',
    linkKey: 'help.link_gene',
    path: '/gene-analysis',
    icon: 'fa-dna',
    iconColor: 'text-success',
  },
  {
    id: 'reports',
    titleKey: 'navigation.reports',
    descKey: 'help.card_reports_desc',
    linkKey: 'help.link_reports',
    path: '/reports',
    loginRequired: true,
    icon: 'fa-chart-bar',
    iconColor: 'text-primary',
  },
  {
    id: 'admin',
    titleKey: 'admin.title',
    descKey: 'admin.how_to_use_desc',
    linkKey: 'help.link_admin',
    path: '/admin',
    adminOnly: true,
    icon: 'fa-cog',
    iconColor: 'text-warning',
  },
];

/** reservation.help.* のセクション ID（表示順・v1 実装に合わせる） */
export const RESERVATION_HELP_SECTIONS = [
  'introduction',
  'sheet_creation',
  'sheet_management',
  'create',
  'edit_delete',
  'calendar',
  'share',
  'tips',
] as const;

export type ReservationHelpSectionId = (typeof RESERVATION_HELP_SECTIONS)[number];

export const HELP_USAGE_GUIDES: HelpUsageGuide[] = [
  {
    id: 'home',
    titleKey: 'home.how_to_use_title',
    kind: 'steps',
    path: '/',
    descKey: 'home.how_to_use_desc',
    stepsKey: ['home.how_to_use_step1', 'home.how_to_use_step2'],
  },
  {
    id: 'dashboard',
    titleKey: 'dashboard.how_to_use_title',
    kind: 'steps',
    path: '/dashboard',
    loginRequired: true,
    descKey: 'dashboard.how_to_use_desc',
    stepsKey: [
      'dashboard.how_to_use_step1',
      'dashboard.how_to_use_step2',
      'dashboard.how_to_use_step3',
      'dashboard.how_to_use_step4',
    ],
  },
  {
    id: 'sample',
    titleKey: 'sample.how_to_use_title',
    kind: 'steps',
    path: '/samples',
    loginRequired: true,
    descKey: 'sample.how_to_use_desc',
    stepsKey: [
      'sample.how_to_use_step1',
      'sample.how_to_use_step2',
      'sample.how_to_use_step3',
      'sample.how_to_use_step4',
      'sample.how_to_use_step5',
    ],
    noteKey: 'sample.how_to_use_note',
  },
  {
    id: 'storage_room',
    titleKey: 'storage_room.how_to_use_title',
    kind: 'steps',
    path: '/storage-rooms',
    loginRequired: true,
    descKey: 'storage_room.how_to_use_desc',
    stepsKey: [
      'storage_room.how_to_use_step1',
      'storage_room.how_to_use_step2',
      'storage_room.how_to_use_step3',
    ],
  },
  {
    id: 'excel_import',
    titleKey: 'excel_import.how_to_use_title',
    kind: 'steps',
    path: '/excel-import',
    loginRequired: true,
    descKey: 'excel_import.how_to_use_desc',
    stepsKey: [
      'excel_import.how_to_use_step1',
      'excel_import.how_to_use_step2',
      'excel_import.how_to_use_step3',
      'excel_import.how_to_use_step4',
    ],
    noteKey: 'excel_import.how_to_use_note',
  },
  {
    id: 'excel_export',
    titleKey: 'excel_export.how_to_use_title',
    kind: 'steps',
    path: '/excel-export',
    loginRequired: true,
    descKey: 'excel_export.how_to_use_desc',
    stepsKey: [
      'excel_export.how_to_use_step1',
      'excel_export.how_to_use_step2',
      'excel_export.how_to_use_step3',
      'excel_export.how_to_use_step4',
    ],
  },
  {
    id: 'reports',
    titleKey: 'reports.how_to_use_title',
    kind: 'steps',
    path: '/reports',
    loginRequired: true,
    descKey: 'reports.how_to_use_desc',
    stepsKey: [
      'reports.how_to_use_step1',
      'reports.how_to_use_step2',
      'reports.how_to_use_step3',
      'reports.how_to_use_step4',
    ],
  },
  {
    id: 'reservation',
    titleKey: 'reservation.help.title',
    kind: 'reservation',
    path: '/reservations',
  },
  {
    id: 'keisuke',
    titleKey: 'keisuke.help.title',
    kind: 'steps',
    path: '/keisuke',
    loginRequired: true,
    descKey: 'keisuke.help.description',
    stepsKey: ['keisuke.help.step1', 'keisuke.help.step2', 'keisuke.help.step3'],
  },
  {
    id: 'gene',
    titleKey: 'geneAnalysis.help.title',
    kind: 'gene',
    path: '/gene-analysis',
    adminOnly: true,
    descKey: 'geneAnalysis.help.content',
    extraKeys: ['geneAnalysis.help.feature1', 'geneAnalysis.help.feature2', 'geneAnalysis.help.feature3'],
  },
  {
    id: 'labo_suke',
    titleKey: 'home.feature_labo_suke_title',
    kind: 'steps',
    path: '/labo-suke',
    descKey: 'home.feature_labo_suke_description',
    stepsKey: [
      'labo_suke.getting_started_step_1',
      'labo_suke.getting_started_step_2',
      'labo_suke.getting_started_step_3',
    ],
    noteKey: 'home.feature_labo_suke_public_hint',
  },
  {
    id: 'gaisuke',
    titleKey: 'gaisuke.arena_about_title',
    kind: 'gaisuke',
    path: '/gaisuke',
    descKey: 'gaisuke.arena_about_lead',
    extraKeys: [
      'gaisuke.arena_about_bullet_schedule',
      'gaisuke.arena_about_bullet_participants',
      'gaisuke.arena_about_bullet_dashboard',
      'gaisuke.arena_about_bullet_media',
      'gaisuke.arena_about_bullet_access',
      'gaisuke.arena_faq_open_link',
    ],
  },
  {
    id: 'transit',
    titleKey: 'home.feature_transit_pass_title',
    kind: 'transit',
    path: '/transit-pass-pool',
    descKey: 'home.feature_transit_pass_description',
    extraKeys: [
      'home.feature_transit_pass_item1',
      'home.feature_transit_pass_item2',
      'home.feature_transit_pass_item3',
      'home.feature_transit_pass_item4',
      'home.feature_transit_pass_item5',
    ],
    noteKey: 'home.feature_transit_pass_public_hint',
  },
  {
    id: 'users',
    titleKey: 'users.how_to_use_title',
    kind: 'steps',
    path: '/users',
    adminOnly: true,
    descKey: 'users.how_to_use_desc',
    stepsKey: ['users.how_to_use_step1', 'users.how_to_use_step2', 'users.how_to_use_step3'],
  },
  {
    id: 'admin',
    titleKey: 'admin.how_to_use_title',
    kind: 'steps',
    path: '/admin',
    adminOnly: true,
    descKey: 'admin.how_to_use_desc',
    stepsKey: [
      'admin.how_to_use_step1',
      'admin.how_to_use_step2',
      'admin.how_to_use_step3',
      'admin.how_to_use_step4',
    ],
  },
  {
    id: 'access_logs',
    titleKey: 'access_logs.help.title',
    kind: 'access_logs',
    path: '/admin/insights/access-logs',
    adminOnly: true,
    descKey: 'access_logs.help.description',
    extraKeys: [
      'access_logs.help.filter',
      'access_logs.help.export',
      'access_logs.help.search',
    ],
  },
];

/** 横断 FAQ の ID（表示順） */
export const HELP_FAQ_IDS = ['permission', 'csrf', 'auth', 'excel', 'reservation_email'] as const;

export type HelpFaqId = (typeof HELP_FAQ_IDS)[number];
